import api from '../config/api';

export interface Expose {
  id: number;
  nom_exposant: string;
  prenom_exposant: string;
  telephone_exposant: string;
  classe: string;
  photo_exposant?: string;
  theme: string;
  description?: string;
  type_expose: string;
  section: 'VCN' | 'HDR' | 'SANTE';
  date_expose: string;
  heure_debut: string;
  heure_fin?: string;
  lieu: string;
  mini_video?: string;
  lien_youtube?: string;
  lien_tiktok?: string;
  lien_instagram?: string;
  audio_complet?: string;
  fichier_pdf?: string;
  status: string;
  status_auto?: string; // Statut calculé automatiquement
  url_slug: string;
  nombre_vues: number;
  nombre_likes: number;
  nombre_commentaires: number;
  is_liked: boolean;
  commentaires_approuves?: Comment[];
  created_at: string;
  updated_at?: string;
}

export interface Comment {
  id: number;
  utilisateur: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    photo?: string;
  };
  contenu: string;
  approuve?: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ExposeFilters {
  type_expose?: string;
  status?: string;
  section?: 'VCN' | 'HDR' | 'SANTE' | '';
  date_expose?: string;
  search?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export const exposeService = {
  // Liste des exposés
  getAll: async (filters?: ExposeFilters): Promise<{ results: Expose[]; count?: number; next?: string; previous?: string }> => {
    const response = await api.get('/exposes/exposes/', { params: filters });
    return response.data;
  },

  // Détails d'un exposé (par ID ou slug)
  getById: async (idOrSlug: number | string): Promise<Expose> => {
    // idOrSlug peut être un ID numérique ou un url_slug (prénom-nom-id)
    const response = await api.get(`/exposes/exposes/${idOrSlug}/`);
    return response.data;
  },

  // Créer un exposé (admin)
  create: async (data: FormData): Promise<Expose> => {
    const response = await api.post('/exposes/exposes/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Modifier un exposé (admin)
  update: async (idOrSlug: number | string, data: FormData): Promise<Expose> => {
    const response = await api.put(`/exposes/exposes/${idOrSlug}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Supprimer un exposé (admin)
  delete: async (idOrSlug: number | string): Promise<void> => {
    await api.delete(`/exposes/exposes/${idOrSlug}/`);
  },

  // Liker/unliker
  like: async (idOrSlug: number | string): Promise<{ liked: boolean }> => {
    const response = await api.post(`/exposes/exposes/${idOrSlug}/like/`);
    return response.data;
  },

  // Commenter
  comment: async (idOrSlug: number | string, contenu: string): Promise<Comment> => {
    const response = await api.post(`/exposes/exposes/${idOrSlug}/commenter/`, { contenu });
    return response.data;
  },

  // Modifier un commentaire
  updateComment: async (commentId: number, contenu: string): Promise<Comment> => {
    const response = await api.put(`/exposes/commentaires/${commentId}/`, { contenu });
    return response.data;
  },

  // Supprimer un commentaire
  deleteComment: async (commentId: number): Promise<void> => {
    await api.delete(`/exposes/commentaires/${commentId}/`);
  },

  // Récupérer tous les commentaires d'un exposé (pour admin)
  getComments: async (exposeId: number): Promise<Comment[]> => {
    const response = await api.get(`/exposes/commentaires/?expose=${exposeId}`);
    return response.data.results || response.data || [];
  },

  // Exposés à venir
  getUpcoming: async (): Promise<Expose[]> => {
    const response = await api.get('/exposes/exposes/upcoming/');
    return response.data;
  },

  // Incrémenter les vues
  incrementView: async (idOrSlug: number | string): Promise<void> => {
    await api.post(`/exposes/exposes/${idOrSlug}/increment_view/`);
  },
};

